import { useState } from "react";
import { Element } from "@nkitaa/builder-engine";
import { motion } from "framer-motion";
import { useWBTheme } from "../theme/WBThemeRuntimeProvider";
import type { BorderSide, RuntimePropsWithChildren } from "./shared";
import {
  SECTION_REVEAL_PROPS,
  ThemeSectionContainer,
  classNames,
  extractUtilityClasses,
  getBorderClasses,
  isVisible,
  parseContentAlignmentClass,
  parseHeroOppositeWidth,
  parseHeroWidth,
  toCssLength,
} from "./shared";
import { TextChild } from "./textChild";
import { IconChild } from "./iconChild";
import { ExpandableProvider } from "./expandableContext";
import { type WBReveal } from "./reveal";
import { AttachmentChips } from "./attachmentChips";

export function AgendaChild({
  isVisible = true,
  isVisibleText1,
  textText1,
  fontSizeText1 = "text-sm",
  fontFamilyText1,
  textAlignText1 = "left",
  textColorText1,
  isVisibleText2,
  textText2,
  fontSizeText2 = "text-sm",
  fontFamilyText2,
  textAlignText2 = "left",
  textColorText2,
  isVisibleText3,
  textText3,
  fontSizeText3 = "text-sm",
  fontFamilyText3,
  textAlignText3 = "left",
  textColorText3,
  isVisibleText,
  textText,
  fontSizeText = "text-sm",
  fontFamilyText,
  textAlignText = "left",
  textColorText,
  backgroundColor = "transparent",
  borderRadius,
  border = true,
  borderColor,
  borderWidth = "border",
  borderStyle = "border-solid",
  borderSides = [],
  padding = "p-3 wb-md:p-4 wb-lg:p-5",
  distTexts = "gap-4",
  distIconText = "gap-4",
  distTitleContent = "mt-2",
  distExpandedText = "mb-2",
  textCount = "3",
  isExpandable = true,
  alwaysOpen = false,
  attachments,
}: {
  isVisible?: boolean;
  isVisibleText1?: boolean;
  textText1?: string;
  fontSizeText1?: string;
  fontFamilyText1?: string;
  textAlignText1?: "left" | "center" | "right" | "justify";
  textColorText1?: string;
  isVisibleText2?: boolean;
  textText2?: string;
  fontSizeText2?: string;
  fontFamilyText2?: string;
  textAlignText2?: "left" | "center" | "right" | "justify";
  textColorText2?: string;
  isVisibleText3?: boolean;
  textText3?: string;
  fontSizeText3?: string;
  fontFamilyText3?: string;
  textAlignText3?: "left" | "center" | "right" | "justify";
  textColorText3?: string;
  isVisibleText?: boolean;
  textText?: string;
  fontSizeText?: string;
  fontFamilyText?: string;
  textAlignText?: "left" | "center" | "right" | "justify";
  textColorText?: string;
  backgroundColor?: string;
  borderRadius?: string;
  border?: boolean;
  borderColor?: string;
  borderWidth?: string;
  borderStyle?: string;
  borderSides?: BorderSide[];
  padding?: string;
  distTexts?: string;
  distIconText?: string;
  distTitleContent?: string;
  distExpandedText?: string;
  textCount?: "1" | "2" | "3";
  isExpandable?: boolean;
  alwaysOpen?: boolean;
  attachments?: unknown;
}) {
  const { currentColorPalette, currentRounding } = useWBTheme();
  const [isOpen, setIsOpen] = useState(false);
  if (!isVisible) return null;
  const showExpanded = isExpandable && (alwaysOpen || isOpen);
  const showText2 = textCount === "2" || textCount === "3";
  const showText3 = textCount === "3";
  const roundedClasses = extractUtilityClasses(
    borderRadius ?? currentRounding,
    "rounded",
  );
  const borderClasses = getBorderClasses(
    border,
    borderWidth,
    borderStyle,
    borderSides,
  );

  return (
    <div
      className={classNames(
        "flex flex-col",
        padding,
        borderClasses,
        roundedClasses,
      )}
      style={{
        transition: "all 300ms ease-in-out",
        backgroundColor:
          backgroundColor !== "transparent" ? backgroundColor : undefined,
        borderColor: borderColor ?? currentColorPalette.secondary,
        borderRadius: roundedClasses
          ? undefined
          : toCssLength(borderRadius ?? currentRounding),
      }}
    >
      {isExpandable ? (
        <div
          className={classNames(
            "w-full flex",
            alwaysOpen ? undefined : "justify-between",
            alwaysOpen
              ? "flex-col wb-md:flex-row wb-md:items-center"
              : "items-center",
            alwaysOpen ? distTexts : distIconText,
          )}
          aria-expanded={alwaysOpen || isOpen}
        >
          <div
            className={classNames(
              "grow flex flex-col wb-md:flex-row wb-md:items-center",
              distTexts,
            )}
          >
            <TextChild
              isVisible={isVisibleText1}
              text={textText1}
              fontSize={fontSizeText1}
              fontFamily={fontFamilyText1}
              textAlign={textAlignText1}
              color={textColorText1}
              width="wb-md:flex-1 min-w-0"
            />
            {showText2 ? (
              <TextChild
                isVisible={isVisibleText2}
                text={textText2}
                fontSize={fontSizeText2}
                fontFamily={fontFamilyText2}
                textAlign={textAlignText2}
                color={textColorText2}
                width="wb-md:flex-1 min-w-0"
                headingLevel="h2"
              />
            ) : null}
            {showText3 ? (
              <TextChild
                isVisible={isVisibleText3}
                text={textText3}
                fontSize={fontSizeText3}
                fontFamily={fontFamilyText3}
                textAlign={textAlignText3}
                color={textColorText3}
                width="wb-md:flex-1 min-w-0"
              />
            ) : null}
          </div>
          {!alwaysOpen ? (
            <div
              className="shrink-0 cursor-pointer"
              role="button"
              tabIndex={0}
              onClick={() => setIsOpen((prev) => !prev)}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === "Enter" || e.key === " ")
                  setIsOpen((prev) => !prev);
              }}
            >
              <ExpandableProvider value={{ isExpanded: showExpanded }}>
                <Element
                  is={IconChild}
                  id="icon"
                  iconName="IconPlus"
                  iconColor={undefined}
                  iconSize="medium"
                  iconAnimationMode="rotate"
                  expandedIconName="IconMinus"
                />
              </ExpandableProvider>
            </div>
          ) : null}
        </div>
      ) : (
        <div
          className={classNames(
            "w-full flex flex-col wb-md:flex-row wb-md:items-center",
            distTexts,
          )}
        >
          <TextChild
            isVisible={isVisibleText1}
            text={textText1}
            fontSize={fontSizeText1}
            fontFamily={fontFamilyText1}
            textAlign={textAlignText1}
            color={textColorText1}
            width="wb-md:flex-1 min-w-0"
          />
          {showText2 ? (
            <TextChild
              isVisible={isVisibleText2}
              text={textText2}
              fontSize={fontSizeText2}
              fontFamily={fontFamilyText2}
              textAlign={textAlignText2}
              color={textColorText2}
              width="wb-md:flex-1 min-w-0"
            />
          ) : null}
          {showText3 ? (
            <TextChild
              isVisible={isVisibleText3}
              text={textText3}
              fontSize={fontSizeText3}
              fontFamily={fontFamilyText3}
              textAlign={textAlignText3}
              color={textColorText3}
              width="wb-md:flex-1 min-w-0"
            />
          ) : null}
        </div>
      )}
      {isExpandable ? (
        <motion.div
          className={classNames(distTitleContent, distExpandedText)}
          initial={false}
          animate={{
            height: showExpanded ? "auto" : 0,
            opacity: showExpanded ? 1 : 0,
          }}
          transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
          style={{ overflow: "hidden" }}
        >
          <TextChild
            isVisible={isVisibleText}
            text={textText}
            fontSize={fontSizeText}
            fontFamily={fontFamilyText}
            textAlign={textAlignText}
            color={textColorText}
          />
          {/* Public attachments (Wave A) — baked, showOnWebsite-filtered. */}
          <AttachmentChips attachments={attachments} className="mt-2" />
        </motion.div>
      ) : null}
    </div>
  );
}

export function Agenda(props: RuntimePropsWithChildren) {
  if (!isVisible(props.isVisible as boolean | undefined)) return null;
  const reveal = props.reveal as WBReveal | undefined;
  const padding =
    (props.padding as string | undefined) ?? "p-4 wb-md:p-6 wb-lg:p-8";
  const sectionMargin = (props.sectionMargin as string | undefined) ?? "py-0";
  const distHeadlineHeader =
    (props.distHeadlineHeader as string | undefined) ?? "mb-2";
  const distHeaderText = (props.distHeaderText as string | undefined) ?? "mb-2";
  const distTextEntries =
    (props.distTextEntries as string | undefined) ?? "gap-3";
  const distEntries = (props.distEntries as string | undefined) ?? "space-y-3";
  const layoutWidth =
    (props.layoutWidth as "100%" | "75%" | "50%" | "33%" | "25%" | undefined) ??
    "100%";
  const inversed = (props.inversed as boolean | undefined) ?? false;
  const alignmentClass = parseContentAlignmentClass(
    props.contentAlignment as "top" | "center" | "bottom" | undefined,
  );
  const leftWidth = parseHeroWidth(layoutWidth);
  const rightWidth = parseHeroOppositeWidth(layoutWidth);
  return (
    <ThemeSectionContainer
      sectionHeight={props.sectionHeight as number | undefined}
      contentAlignment={
        props.contentAlignment as "top" | "center" | "bottom" | undefined
      }
      backgroundColor={props.backgroundColor as string | undefined}
      padding={padding}
      sectionMargin={sectionMargin}
      border={props.border as boolean | undefined}
      borderColor={props.borderColor as string | undefined}
      borderWidth={props.borderWidth as string | undefined}
      borderStyle={props.borderStyle as string | undefined}
      borderSides={props.borderSides as BorderSide[] | undefined}
      borderRadius={props.borderRadius as string | undefined}
      reveal={reveal}
    >
      <motion.div
        {...SECTION_REVEAL_PROPS}
        className={classNames(
          "items-center max-w-275 w-full flex flex-col",
          alignmentClass,
        )}
      >
        <div
          className={classNames(
            "flex w-full",
            distTextEntries,
            layoutWidth !== "100%"
              ? inversed
                ? "flex-col wb-md:flex-row-reverse gap-4"
                : "flex-col wb-md:flex-row gap-4"
              : inversed
                ? "flex-col-reverse"
                : "flex-col",
          )}
        >
          <div
            className={classNames(
              "flex flex-col grow justify-center",
              leftWidth,
            )}
          >
            <div className={distHeadlineHeader}>
              <Element
                is={TextChild}
                id="headline"
                isVisible={props.isVisibleHeadline as boolean | undefined}
                text={props.textHeadline as string | undefined}
                fontSize={props.fontSizeHeadline as string | undefined}
                textAlign={
                  props.textAlignHeadline as
                    | "left"
                    | "center"
                    | "right"
                    | "justify"
                    | undefined
                }
                header
                headingLevel="h2"
              />
            </div>
            <div className={distHeaderText}>
              <Element
                is={TextChild}
                id="header"
                isVisible={props.isVisibleHeader as boolean | undefined}
                text={props.textHeader as string | undefined}
                fontSize={props.fontSizeHeader as string | undefined}
                textAlign={
                  props.textAlignHeader as
                    | "left"
                    | "center"
                    | "right"
                    | "justify"
                    | undefined
                }
                header
                headingLevel="h1"
              />
            </div>
            <Element
              is={TextChild}
              id="text"
              isVisible={props.isVisibleText as boolean | undefined}
              text={props.textText as string | undefined}
              fontSize={props.fontSizeText as string | undefined}
              textAlign={
                props.textAlignText as
                  | "left"
                  | "center"
                  | "right"
                  | "justify"
                  | undefined
              }
            />
          </div>
          <div className={classNames("flex flex-col", rightWidth, distEntries)}>
            {props.children}
          </div>
        </div>
      </motion.div>
    </ThemeSectionContainer>
  );
}
